/**
 * Import de membres depuis un fichier Excel (.xlsx) ou CSV — lecture côté
 * navigateur, l'API ne reçoit que la liste {email, role}.
 *
 * Colonnes reconnues par leur en-tête (« E-mail », « Statut », « Rôle »…),
 * sinon : la colonne qui contient des adresses, puis la colonne suivante pour
 * le statut. Le statut est traduit en rôle Placio (Membre ou Admin) — une
 * valeur inconnue donne Membre, signalée dans l'aperçu pour être corrigée.
 */

export type ImportRole = 'MEMBER' | 'ADMIN';

export interface ImportRow {
  /** Numéro de ligne dans le fichier (1 = première ligne). */
  line: number;
  email: string;
  /** Valeur brute de la colonne statut/rôle, telle que dans le fichier. */
  rawStatus: string;
  role: ImportRole;
  /** Accès lus dans le fichier (ex. « Copy Camp seul », « CH Asc Mail »). */
  accessLabels: string[];
  /** Raison pour laquelle la ligne ne sera pas envoyée. */
  skip?: 'invalid' | 'duplicate' | 'exists';
  /** Remarque affichée (statut non reconnu, propriétaire…). */
  note?: string | undefined;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_HEADER_RE = /mail|courriel|adresse/;
const STATUS_HEADER_RE = /statut|status|role/;
const ACCESS_HEADER_RE = /acces|profil|droit/;
const YES_RE = /^(oui|x|yes|vrai|true|1)$/;

export function normalize(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function cellText(cell: unknown): string {
  if (cell === null || cell === undefined) return '';
  return String(cell).trim();
}

/** Statut du fichier → rôle Placio. */
export function statusToRole(raw: string): { role: ImportRole; note?: string } {
  const s = normalize(raw);
  if (!s) return { role: 'MEMBER' };
  if (/^(proprietaire|owner)/.test(s)) {
    return { role: 'ADMIN', note: 'Propriétaire : invité en Admin, à passer propriétaire ensuite depuis la liste.' };
  }
  if (/admin|gestionnaire/.test(s)) return { role: 'ADMIN' };
  if (/^(membre|member|utilisat|user|collaborat)/.test(s)) return { role: 'MEMBER' };
  return { role: 'MEMBER', note: `Statut « ${raw} » non reconnu : Membre par défaut.` };
}

/**
 * Lignes brutes du tableur → lignes d'import, avec doublons et comptes
 * existants repérés (`existingEmails` en minuscules).
 */
export function buildImportRows(sheet: unknown[][], existingEmails: Set<string>): ImportRow[] {
  const rows = sheet.map((r) => (Array.isArray(r) ? r.map(cellText) : []));

  // En-tête : une des premières lignes qui nomme une colonne e-mail.
  let headerIndex = -1;
  let emailCol = -1;
  let statusCol = -1;
  let accessCol = -1;
  let yesCols: { col: number; label: string }[] = [];
  for (let i = 0; i < Math.min(rows.length, 5); i++) {
    const cells = rows[i]!.map(normalize);
    const e = cells.findIndex((c) => EMAIL_HEADER_RE.test(c) && !c.includes('@'));
    if (e >= 0) {
      headerIndex = i;
      emailCol = e;
      statusCol = cells.findIndex((c, idx) => idx !== e && STATUS_HEADER_RE.test(c));
      accessCol = cells.findIndex((c, idx) => idx !== e && idx !== statusCol && ACCESS_HEADER_RE.test(c));
      if (accessCol < 0) {
        // Une colonne par accès : en-tête = nom de l'accès, cases « oui » ou vides.
        yesCols = rows[i]!.map((label, col) => ({ col, label: label.trim() })).filter(({ col, label }) => {
          if (!label || col === e || col === statusCol) return false;
          const values = rows.slice(i + 1).map((r) => normalize(r[col] ?? '')).filter(Boolean);
          return values.length > 0 && values.every((v) => YES_RE.test(v));
        });
      }
      break;
    }
  }

  // Pas d'en-tête : la colonne qui contient le plus d'adresses, puis la suivante non vide.
  if (headerIndex < 0) {
    const width = Math.max(0, ...rows.map((r) => r.length));
    let best = 0;
    for (let c = 0; c < width; c++) {
      const count = rows.filter((r) => (r[c] ?? '').includes('@')).length;
      if (count > best) { best = count; emailCol = c; }
    }
    if (emailCol < 0) return [];
    for (let c = 0; c < width; c++) {
      if (c !== emailCol && rows.some((r) => r[c])) { statusCol = c; break; }
    }
  }

  const seen = new Set<string>();
  const result: ImportRow[] = [];
  rows.forEach((r, i) => {
    if (i <= headerIndex) return;
    const email = (r[emailCol] ?? '').trim().toLowerCase();
    const rawStatus = statusCol >= 0 ? (r[statusCol] ?? '') : '';
    if (!email && !rawStatus) return; // ligne vide
    const { role, note } = statusToRole(rawStatus);
    const accessLabels = accessCol >= 0
      ? [...new Set((r[accessCol] ?? '').split(/[+,;|]/).map((v) => v.trim()).filter(Boolean))]
      : yesCols.filter(({ col }) => YES_RE.test(normalize(r[col] ?? ''))).map(({ label }) => label);
    const row: ImportRow = { line: i + 1, email, rawStatus, role, accessLabels, ...(note ? { note } : {}) };
    if (!EMAIL_RE.test(email)) row.skip = 'invalid';
    else if (seen.has(email)) row.skip = 'duplicate';
    else if (existingEmails.has(email)) row.skip = 'exists';
    seen.add(email);
    result.push(row);
  });
  return result;
}

/** CSV simple (séparateur `;`, `,` ou tabulation, guillemets gérés). */
export function parseCsv(text: string): string[][] {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = [';', '\t', ','].reduce((a, b) => (firstLine.split(b).length > firstLine.split(a).length ? b : a));
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { row.push(field); field = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); rows.push(row); row = []; field = '';
    } else field += ch;
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows;
}

/** Lit un fichier .xlsx ou .csv en tableau de lignes (première feuille). */
export async function readSpreadsheet(file: File): Promise<unknown[][]> {
  if (/\.(csv|txt)$/i.test(file.name)) {
    return parseCsv((await file.text()).replace(/^﻿/, ''));
  }
  // Chargé à la demande : seul un OWNER qui importe un fichier en a besoin.
  const { readSheet } = await import('read-excel-file/browser');
  return (await readSheet(file)) as unknown[][];
}
